<script lang="ts" setup>
import {computed, ref, toRefs} from 'vue';
import {useSession} from '/src/core/session';
import {logEvent} from '/src/utils';

const session = useSession();
const {state} = session;
const {auth, subscription} = toRefs(state);

const isAnnual = ref(false);

const isSubscriptionActive = computed(() => !!subscription.value && subscription.value?.status !== 'expired');

function logBuyBasic() {
    logEvent('button_buy', 'BASIC');
}

function logBuyPlus() {
    logEvent('button_buy', 'PLUS');
}

function logSignUp() {
    logEvent('button_sign_in', 'PLUS');
}

function logContactSales() {
    logEvent('button_buy', 'ENTERPRISE');
}

function logContactEdu() {
    logEvent('button_buy', 'EDUCATION');
}

function buyPlus() {
    if (!auth.value) {
        return;
    }
    const {email, id} = auth.value.user;
    const plusURL = `${import.meta.env.VITE_CHECKOUT_URL}?checkout[email]=${email}&checkout[custom][user_id]=${id}`;
    window.location.href = plusURL;
}

function toggleAnnual() {
    isAnnual.value = !isAnnual.value;
}
</script>

<template>
    <slot></slot>
    <div class="flex flex-col mt-4 justify-around gap-8 lg:gap-8 lg:flex-row flex-col xl:flex-nowrap">
        <!-- Basic Plan -->
        <div class="rounded-2xl shadow-2xl px-6 py-4 2xl:px-12 2xl:py-8 border-2 border-neutral lg:w-1/3 w-full">
            <p class="font-bold uppercase tracking-wider">Basic</p>
            <p class="mt-2">
                <span class="text-4xl font-extrabold text-primary">FREE</span>
            </p>

            <ul class="mt-8 text-left space-y-3">
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3">✔</span>
                    <span class="">Code Generation</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3">✔</span>
                    <span class="">Image Processing</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3">✔</span>
                    <span class="">1 Public Project</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3">✔</span>
                    <span class="">5 Public Screens</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3">✔</span>
                    <span class="">Community Fonts</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3">✔</span>
                    <span class="">Community Icons</span>
                </li>
            </ul>

            <div
                v-if="auth && !isSubscriptionActive"
                class="btn btn-ghost text-gray-500 w-full mt-8 uppercase"
            >
                Current plan
            </div>
            <router-link
                v-if="!auth"
                class="btn btn-outline btn-primary w-full mt-8 uppercase"
                @click="logBuyBasic"
                to="/login"
            >
                Sign Up
            </router-link>
        </div>

        <!-- Plus Plan -->
        <div class="rounded-2xl shadow-2xl px-6 py-4 2xl:px-12 2xl:py-8 border-4 border-primary lg:w-1/3 w-full">
            <p class="font-bold uppercase text-success tracking-wider">Plus</p>
            <p
                class="mt-2"
                v-if="isAnnual"
            >
                <span class="text-4xl font-extrabold text-success">$70</span>
                <span class="text-xl font-medium">&nbsp;/ year</span>
            </p>
            <p
                class="mt-2"
                v-else
            >
                <span class="text-4xl font-extrabold text-success">$12</span>
                <span class="text-xl font-medium">&nbsp;/ month</span>
            </p>

            <div class="mt-4">
                <span
                    v-if="isAnnual"
                    class="text-primary cursor-pointer underline"
                    @click="toggleAnnual"
                >
                    Or pay monthly
                </span>
                <span
                    v-else
                    class="text-primary cursor-pointer underline"
                    @click="toggleAnnual"
                >
                    Save 50% with annual payment
                </span>
            </div>

            <ul class="mt-8 text-left space-y-3">
                <li class="flex items-center">
                    <span class="text text-xl mr-3">✔</span>
                    <span class="">Code Generation</span>
                </li>
                <li class="flex items-center">
                    <span class="text text-xl mr-3">✔</span>
                    <span class="">Image Processing</span>
                </li>
                <li class="flex items-center">
                    <span class="text text-xl mr-3">✔</span>
                    <span class="">Community Fonts</span>
                </li>
                <li class="flex items-center">
                    <span class="text text-xl mr-3">✔</span>
                    <span class="">Community Icons</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">Unlimited Projects</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">Unlimited Screens</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">Privacy settings</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">Custom Fonts</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">Export options</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">Presentation mode</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">Priority Support</span>
                </li>
            </ul>

            <div
                v-if="auth && isSubscriptionActive"
                class="btn btn-ghost text-gray-500 w-full mt-8 uppercase"
            >
                Current plan
            </div>
            <button
                v-if="auth && !isSubscriptionActive"
                class="btn btn-success w-full mt-8 uppercase"
                @click="
                    logBuyPlus();
                    buyPlus();
                "
            >
                Buy Now
            </button>
            <router-link
                to="/login#buy"
                v-if="!auth && !isSubscriptionActive"
                class="btn btn-success w-full mt-8 uppercase"
                @click="logSignUp"
            >
                Sign Up
            </router-link>
        </div>

        <!-- Enterprise Plan -->
        <div class="rounded-2xl shadow-2xl px-6 py-4 2xl:px-12 2xl:py-8 border-4 border-gray-400 lg:w-1/3 w-full">
            <p class="font-bold uppercase text-gray-400 tracking-wider">Enterprise</p>
            <p class="mt-4 text-sm text-base-content opacity-75">
                Tailored solutions for hardware companies, IoT firms, and development teams
            </p>

            <ul class="mt-8 text-left space-y-3">
                <li class="flex items-center">
                    <span class="font-bold">Everything in Plus and:</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">Team Collaboration</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">Custom Platform Support</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">API Access & Integrations</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">Custom features</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">SSO & User Management</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">On-premise deployment</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">White-label Options</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">Volume Licensing</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">Daily Backups</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">Training & Onboarding</span>
                </li>
                <li class="flex items-center">
                    <span class="text-green-500 text-xl mr-3 font-extrabold">✔</span>
                    <span class="font-bold">Dedicated Support</span>
                </li>
            </ul>

            <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSeUhG8AIPUzPO3kZ755WSrCf5oHtq1MXLGxJu1pTsaP5hRaPQ/viewform?usp=header"
                target="_blank"
                class="btn bg-gray-400 text-neutral w-full mt-8 uppercase hover:bg-white"
                @click="logContactSales"
            >
                Request a quote
            </a>
        </div>
    </div>

    <!-- Educational Section -->
    <div class="mt-8 p-8 bg-base-200 rounded-2xl">
        <div class="text-center">
            <h3 class="text-2xl font-bold text-info mb-4">🎓 Lopaka for Classrooms</h3>
            <p class="text-base-content text-lg">
                Are you a teacher looking to use Lopaka Plus for educational purposes?
            </p>
            <p class="text-base-content text-lg mb-6">
                We provide
                <strong class="text-success">free Plus access for 1 year</strong>
                to educators who want to use Lopaka in their classrooms.
            </p>
            <a
                href="mailto:edu@lopaka.app?subject=Educational%20Access%20Request"
                class="btn btn-outline btn-info uppercase px-8"
                @click="logContactEdu"
            >
                Contact us
            </a>
        </div>
    </div>
</template>
